import { graphql } from 'gatsby';

import {
  Button,
  Grid2 as Grid,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { PersonTile, ResponsiveGrid } from '@mewa/ui';

import { markdownToHTML } from '../../../../libs/markdown-to-html';
import { useShowMoreItems } from '../../../helpers/use-show-more-items';
import { ShowMoreButtonContainer } from '../../blog/components/article-teaser-medium';
import { ComponentProps, PossibleContentSections } from '../fragment-and-types';
import { ContentLivePreviewConfig } from '../live-preview/types';
import { StrapiImage, StrapiImageData } from './image';

export const PersonTileFragment = graphql`
  fragment PersonTileFragment on STRAPI__COMPONENT_SHARED_PERSON_TILE {
    title
    items: personTile {
      name
      html {
        data {
          childMarkdownRemark {
            html
          }
        }
      }
      imageAlternativeText
      image {
        ...StrapiImageFileFragment
      }
    }
    strapi_component
  }
`;

export const componentName = 'shared.person-tile';

type Item = {
  name: string;
  imageAlternativeText: string;
  image: StrapiImageData;
  html: {
    data: {
      childMarkdownRemark: { html: string };
    };
  };
};

export type PersonTileData = {
  items: Item[];
  strapi_component: typeof componentName;
  title: string;
};

export type PersonTilePreviewData = {
  strapi_component: typeof componentName;
  title: string;
  personTile: {
    name: string;
    imageAlternativeText: string;
    image: StrapiImageData;
    html: string;
  }[];
};

export const isPersonTile = (
  component: PossibleContentSections,
): component is PersonTileData => {
  return component.strapi_component === componentName;
};

const translations = {
  show_more: '',
  show_less: '',
};

export const setShowMoreLessTranslations = (more: string, less: string) => {
  translations.show_more = more;
  translations.show_less = less;
};

export const StrapiPersonTile = ({
  items,
  title,
}: ComponentProps<PersonTileData>) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const is900 = useMediaQuery(theme.breakpoints.down('md'));

  const initialArticleCount = isMobile ? 2 : is900 ? 6 : 8;

  const { itemCount, loadMoreItems, showLessItems } = useShowMoreItems({
    initialCount: initialArticleCount,
    showMoreCount: items.length,
  });

  return (
    <ResponsiveGrid>
      <Grid size={12}>
        <Headline>{title}</Headline>
      </Grid>
      {items.slice(0, itemCount).map((item, index) => (
        <PersonTile
          key={index}
          image={<Image alt={item.imageAlternativeText} image={item.image} />}
          name={item.name}
          content={item.html.data.childMarkdownRemark.html}
        />
      ))}

      <ShowMoreButtonContainer
        sx={{
          marginBottom: 0,
          marginTop: { xs: '20px', sm: 'var(--spacer-l)' },
          justifyContent: 'center',
        }}
      >
        {itemCount < items.length ? (
          <Button variant="text" onClick={loadMoreItems}>
            {translations.show_more}
          </Button>
        ) : (
          <Button variant="text" onClick={() => showLessItems(true)}>
            {translations.show_less}
          </Button>
        )}
      </ShowMoreButtonContainer>
    </ResponsiveGrid>
  );
};

const Headline = styled('h2')(({ theme }) => ({
  ...theme.typography.h2,
  textAlign: 'center',
  margin: '20px 0 40px 0',

  [theme.breakpoints.up('md')]: {
    marginBottom: '60px',
  },
}));

const Image = styled(StrapiImage)(({ theme }) => ({
  width: '180px',
  margin: '0 auto',
  borderRadius: '50%',

  [theme.breakpoints.up('sm')]: {
    width: '200px',
  },

  [theme.breakpoints.up('md')]: {
    width: '250px',
  },
}));

export const config = {
  isComponent: isPersonTile,
  Component: StrapiPersonTile,
  componentName,
};

export const previewConfig: ContentLivePreviewConfig = {
  populationParams: { personTile: { populate: { image: '*' } } },
  componentName,
  mapProperties: (personTile): PersonTileData | null =>
    personTile.strapi_component === componentName
      ? {
          ...personTile,
          items: (personTile as PersonTilePreviewData).personTile.map(
            (item) => ({
              ...item,
              html: markdownToHTML(item.html),
            }),
          ),
        }
      : null,
};
